import {
  TextField,
  Select,
  MenuItem,
  Typography,
  FormControlLabel,
  InputLabel,
  RadioGroup,
  Radio,
  Tooltip,
  TooltipProps,
  tooltipClasses,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormHelperText,
  Alert,
  Box
} from '@mui/material';
import {ReactElement, useMemo, useCallback} from 'react';
import styled from '@emotion/styled';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  ALERT_SETTINGS_CONFIG,
  MONITORING_ITEM_VALUES,
  getAvailableIntervals,
  getAvailableRules
} from '../constants/alertSettingsConfig';

interface ValidationErrors {
  alertName?: string;
  machine?: string;
  from?: string;
  to?: string;
  fixTime?: string;
  timeRange?: string;
}

interface Device {
  label: string;
}

interface AlertSettingsStep1Props {
  active: boolean;
  setActive: (value: boolean) => void;
  alertName: string;
  machine: string;
  processType: string;
  setProcessType: (value: string) => void;
  rule: string;
  setRule: (value: string) => void;
  scheduleType: string;
  days: string[];
  setDays: (value: string[]) => void;
  from: string;
  to: string;
  fixTime: string;
  interval: string;
  isPdf: boolean;
  setIsPdf: (value: boolean) => void;
  devices: Device[];
  DAYS_MAP: Record<string, string>;
  t: (key: string) => string;
  validationErrors: ValidationErrors;
  handleAlertNameChange: (value: string) => void;
  handleMachineChange: (value: string) => void;
  handleFromChange: (value: string) => void;
  handleToChange: (value: string) => void;
  handleFixTimeChange: (value: string) => void;
  handleIntervalChange: (value: string) => void;
  handleScheduleTypeChange: (value: string) => void;
}

interface HelpTooltipProps extends Omit<TooltipProps, 'children'> {
  title: string;
  children?: ReactElement;
}

const HelpTooltip = styled(({className, ...props}: HelpTooltipProps) => (
  <Tooltip {...props} classes={{popper: className}}>
    <span>
      <HelpOutlineIcon />
    </span>
  </Tooltip>
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '14px',
    whiteSpace: 'pre-line'
  }
}));

export const AlertSettingsBasic = ({
  active,
  setActive,
  alertName,
  machine,
  processType,
  setProcessType,
  rule,
  setRule,
  scheduleType,
  days,
  setDays,
  from,
  to,
  fixTime,
  interval,
  isPdf,
  setIsPdf,
  devices,
  DAYS_MAP,
  t,
  validationErrors,
  handleAlertNameChange,
  handleMachineChange,
  handleFromChange,
  handleToChange,
  handleFixTimeChange,
  handleIntervalChange,
  handleScheduleTypeChange
}: AlertSettingsStep1Props) => {
  // コールバック関数をメモ化してパフォーマンスを最適化
  const handleDaysChange = useCallback(
    (event: any, newDays: string[]) => {
      if (newDays.length > 0) {
        setDays(newDays);
      }
    },
    [setDays]
  );

  return (
    <>
      {/* 監視の実行 */}
      <Box
        sx={{
          marginBottom: 2,
          border: '3px solid',
          width: '100%',
          borderRadius: 2.5,
          padding: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <Typography fontSize={20} marginBottom={3}>
            {t('監視の実行')}
          </Typography>
          <HelpTooltip
            title={t(
              '有効を選択時は監視を行い、通知者へ監視結果を送信します。無効を選択時は監視や結果送信は行いませんが、設定は残ります。'
            )}
          />
        </Box>

        <RadioGroup row>
          <FormControlLabel
            value="enabled"
            control={<Radio checked={active} onChange={() => setActive(!active)} />}
            label={t('有効')}
          />
          <FormControlLabel
            value="disabled"
            control={<Radio checked={!active} onChange={() => setActive(!active)} />}
            label={t('無効')}
          />
        </RadioGroup>
      </Box>

      {/* アラート名 */}
      <Box
        sx={{
          marginBottom: 2,
          border: '3px solid',
          width: '100%',
          borderRadius: 2.5,
          padding: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <Typography fontSize={20} marginBottom={3}>
            {t('アラート名')}
          </Typography>
          <HelpTooltip
            title={t('この設定に対して任意に名前を付けられます。通知の件名として使用されます。')}
          />
        </Box>
        <TextField
          label={t('アラート名')}
          value={alertName}
          onChange={e => handleAlertNameChange(e.target.value)}
          inputProps={{maxLength: 100}}
          fullWidth
          error={!!validationErrors.alertName}
          helperText={validationErrors.alertName}
          required
        />
      </Box>

      {/* 監視対象 */}
      <Box
        sx={{
          marginBottom: 2,
          border: '3px solid',
          width: '100%',
          borderRadius: 2.5,
          padding: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <Typography fontSize={20} marginBottom={3}>
            {t('監視対象')}
          </Typography>
          <HelpTooltip
            title={t(
              '監視する装置及び対象のデータを選択します。どの装置で収集しているデータであるか、その中でどのデータを監視するかを選択します。'
            )}
          />
        </Box>
        <FormControl fullWidth error={!!validationErrors.machine} sx={{marginBottom: 2.5}}>
          <InputLabel required>{t('監視装置')}</InputLabel>
          <Select
            value={machine}
            onChange={e => handleMachineChange(e.target.value)}
            label={t('監視装置')}
          >
            {devices.map(item => (
              <MenuItem value={item.label} key={item.label}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
          {validationErrors.machine && <FormHelperText>{validationErrors.machine}</FormHelperText>}
        </FormControl>

        <FormControl fullWidth sx={{marginBottom: 2.5}}>
          <InputLabel>{t('監視アイテム')}</InputLabel>
          <Select
            value={processType}
            onChange={e => setProcessType(e.target.value)}
            label={t('監視アイテム')}
          >
            <MenuItem value={MONITORING_ITEM_VALUES.DEFECT_RATIO}>
              {t('不良率 （本日分）：不良製品数/検査製品数')}
            </MenuItem>
            {ALERT_SETTINGS_CONFIG.monitoring.showDefectRateToday && (
              <MenuItem value={MONITORING_ITEM_VALUES.DEFECT_RATE}>
                {t('欠点率（本日分）：不良部位数/検査部位数')}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>

      {/* 判定ルール */}
      <Box
        sx={{
          marginBottom: 2,
          border: '3px solid',
          width: '100%',
          borderRadius: 2.5,
          padding: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <Typography fontSize={20} marginBottom={3}>
            {t('判定ルール')}
          </Typography>
          <HelpTooltip
            title={t(
              '監視アイテムについて異常等の判定ルールを選択します。\n3σ異常判定は平均と標準偏差σを算出します。\n平均+1σ以下を「安定」、平均+1～2σを「不安定」、平均+2～3σを「警告」、平均+3σ(UCL)以上を異常として「危険」と各々表記して通知します。'
            )}
          />
        </Box>
        <FormControl fullWidth sx={{marginBottom: 2.5}}>
          <InputLabel>{t('判定ルール')}</InputLabel>
          <Select value={rule} onChange={e => setRule(e.target.value)} label={t('判定ルール')}>
            {getAvailableRules().map(ruleItem => (
              <MenuItem key={ruleItem.value} value={ruleItem.value}>
                {t(ruleItem.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* 監視スケジュール */}
      <Box
        sx={{
          marginBottom: 2,
          border: '3px solid',
          width: '100%',
          borderRadius: 2.5,
          padding: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <Typography fontSize={20} marginBottom={3}>
            {t('監視スケジュール')}
          </Typography>
          <HelpTooltip
            title={t(
              '監視作業を行う日時を設定します。監視したい日の曜日を選択してください。判定時刻で「定期」を選択した場合、指定された開始時刻から終了時刻の期間を繰り返し監視します。指定曜日内で複数回の監視判定を行いたい場合は「定期」で設定してください。判定時刻で「固定」を選択した場合、選択された曜日の指定時刻にて１度判定を行います。'
            )}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {t('曜日選択')}
          </Typography>
          <ToggleButtonGroup
            value={days}
            onChange={handleDaysChange}
            aria-label="day selection"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5
            }}
          >
            {Object.keys(DAYS_MAP).map(dayJp => (
              <ToggleButton
                key={dayJp}
                value={DAYS_MAP[dayJp]}
                aria-label={`${dayJp}曜日を選択`}
                aria-pressed={days.includes(DAYS_MAP[dayJp])}
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  border: '1px solid',
                  borderColor: 'text.primary',
                  color: 'text.primary',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      borderColor: 'primary.dark'
                    }
                  }
                }}
              >
                {t(`${dayJp}曜`).replace('曜', '')}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2}}>
          <Typography> {t('判定時刻')}</Typography>

          {/* スケジュールタイプ選択 */}
          <RadioGroup
            row
            value={scheduleType}
            onChange={e => handleScheduleTypeChange(e.target.value)}
          >
            <FormControlLabel value="1" control={<Radio />} label={t('定期')} />
            <FormControlLabel value="2" control={<Radio />} label={t('固定')} />
          </RadioGroup>

          {/* 定期の場合の入力フィールド */}
          {scheduleType === '1' && (
            <>
              <Box>
                <TextField
                  label={t('期間From')}
                  type="time"
                  value={from}
                  onChange={e => handleFromChange(e.target.value)}
                  sx={{width: '120px'}}
                  error={!!validationErrors.from}
                  helperText={validationErrors.from}
                  required
                />
                <TextField
                  label={t('期間To')}
                  type="time"
                  value={to}
                  onChange={e => handleToChange(e.target.value)}
                  sx={{width: '120px', marginLeft: 1}}
                  error={!!validationErrors.to}
                  helperText={validationErrors.to}
                  required
                />
              </Box>

              <Box sx={{width: '256px'}}>
                <FormControl fullWidth>
                  <InputLabel>{t('判定頻度')}</InputLabel>
                  <Select
                    value={interval}
                    onChange={e => handleIntervalChange(e.target.value)}
                    label={t('判定頻度')}
                  >
                    {getAvailableIntervals().map(item => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.hours}
                        {t('時間毎')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* 時刻範囲バリデーションエラー */}
              {validationErrors.timeRange && (
                <Alert severity="warning" sx={{mt: 1}}>
                  {validationErrors.timeRange}
                </Alert>
              )}
            </>
          )}

          {/* 固定の場合の入力フィールド */}
          {scheduleType === '2' && (
            <Box>
              <TextField
                label={t('時刻')}
                type="time"
                value={fixTime}
                onChange={e => handleFixTimeChange(e.target.value)}
                sx={{width: '120px'}}
                error={!!validationErrors.fixTime}
                helperText={validationErrors.fixTime}
                required
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* PDFレポート（メールファイル添付） */}
      <Box
        sx={{
          marginBottom: 2,
          border: '3px solid',
          width: '100%',
          borderRadius: 2.5,
          padding: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <Typography fontSize={20} marginBottom={3}>
            {t('PDFレポート（メールファイル添付）')}
          </Typography>
          <HelpTooltip
            title={t(
              'アラートメールは標準としてテキストのみで送信されます。「添付する」を選択した場合、メールにPDFファイルが添付されます。PDFファイルの内容はメール本文と同じものが記載されますが、関連するグラフと分析コメントが追加で記載されます。分析コメントはAIによる分析となります。'
            )}
          />
        </Box>

        <RadioGroup
          row
          value={isPdf ? 'true' : 'false'}
          onChange={e => setIsPdf(e.target.value === 'true')}
        >
          <FormControlLabel value="true" control={<Radio />} label={t('添付する')} />
          <FormControlLabel value="false" control={<Radio />} label={t('添付しない')} />
        </RadioGroup>
      </Box>
    </>
  );
};
